// API utility with automatic token refresh on 401 errors

const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL}`;

// Mutex to prevent concurrent refresh attempts
let refreshInProgress = false;
let refreshPromise = null;

// Refresh the access token using the refresh token -> Promise<{access_token, expires, refresh_token} | null>
export const refreshAccessToken = async () => {
  // If refresh already in progress, wait for it instead of starting a new one
  if (refreshInProgress && refreshPromise) {
    return refreshPromise;
  }

  // Mark refresh as in progress and create the promise
  refreshInProgress = true;
  refreshPromise = (async () => {
    // Store the original refresh token before making the request
    const originalRefreshToken = sessionStorage.getItem('refresh_token');
    
    try {
      if (!originalRefreshToken) {
        return null;
      }
      
      // Use regular fetch (not apiFetch) to avoid infinite loops
      const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'accept': 'application/json'
        },
        body: JSON.stringify({
          token: originalRefreshToken
        })
      });

      if (!response.ok) {
        const currentRefreshToken = sessionStorage.getItem('refresh_token');
        if (currentRefreshToken === originalRefreshToken) {
          sessionStorage.removeItem('token');
          sessionStorage.removeItem('refresh_token');
          sessionStorage.removeItem('expires');
        } else {
        }
        return null;
      }

      const data = await response.json();
      
      // Update stored tokens
      if (data.access_token) {
        sessionStorage.setItem('token', data.access_token);
      }
      
      if (data.expires) {
        const currentTime = new Date().getTime();
        const expirationTime = currentTime + parseInt(data.expires, 10);
        sessionStorage.setItem('expires', expirationTime.toString());
      }
      
      if (data.refresh_token) {
        sessionStorage.setItem('refresh_token', data.refresh_token);
      }

      return data;
    } catch (error) {
      // Only clear tokens if refresh token is still the same
      const currentRefreshToken = sessionStorage.getItem('refresh_token');
      if (currentRefreshToken === originalRefreshToken) {
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('refresh_token');
        sessionStorage.removeItem('expires');
      } else {
      }
      return null;
    } finally {
      // Reset the mutex
      refreshInProgress = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

// Check if token is expired -> boolean
const isTokenExpired = () => {
  const expires = sessionStorage.getItem('expires');
  if (!expires) return true;
  
  const expirationTime = parseInt(expires, 10);
  const currentTime = new Date().getTime();
  
  return currentTime >= expirationTime;
};

// Enhanced fetch with automatic token refresh on 401 - (url: string, options?: RequestInit, retry?: boolean) -> Promise<Response>
export const apiFetch = async (url, options = {}, retry = true) => {
  const token = sessionStorage.getItem('token');
  
  // Add authorization header if token exists
  const headers = {
    ...options.headers,
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Make the request
  let response = await fetch(url, {
    ...options,
    headers
  });

  // Handle 401 Unauthorized - try to refresh token (only once)
  if (response.status === 401 && retry) {
    // Check if there's an active stream/job - don't redirect during active processing
    const activeJobId = sessionStorage.getItem('activeJobId');
    const hasActiveStream = activeJobId !== null && activeJobId !== '';
    
    const refreshToken = sessionStorage.getItem('refresh_token');
    
    if (refreshToken) {
      // Attempt to refresh the token
      const refreshData = await refreshAccessToken();
      
      // Always get latest token from sessionStorage after refresh (ensures we use most up-to-date token even if multiple refreshes happened)
      const newToken = sessionStorage.getItem('token');
      
      if (refreshData && newToken) {
        // Retry the original request with the new token from sessionStorage Reading from sessionStorage ensures we use the latest token even if multiple refreshes happened concurrently
        const newHeaders = {
          ...options.headers,
          'Authorization': `Bearer ${newToken}`
        };
        
        response = await fetch(url, {
          ...options,
          headers: newHeaders
        });
        
        // If retry also fails with 401, don't retry again
        if (response.status === 401) {
          // Refresh token might be invalid - clear everything
          sessionStorage.removeItem('token');
          sessionStorage.removeItem('refresh_token');
          sessionStorage.removeItem('expires');
          
          // Don't redirect if there's an active stream - let it handle the error gracefully
          if (!hasActiveStream && window.location.pathname !== '/login') {
            window.location.href = '/login';
          } else if (hasActiveStream) {
          }
        } else {
        }
      } else {
        console.error('[API Fetch] Token refresh failed');
        // Refresh failed - clear tokens
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('refresh_token');
        sessionStorage.removeItem('expires');
        
        // Don't redirect if there's an active stream - let it handle the error gracefully
        if (!hasActiveStream && window.location.pathname !== '/login') {
          window.location.href = '/login';
        } else if (hasActiveStream) {
        }
      }
    } else {
      console.warn('[API Fetch] No refresh token available');
      // No refresh token - clear tokens
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('refresh_token');
      sessionStorage.removeItem('expires');
      
      // Don't redirect if there's an active stream - let it handle the error gracefully
      if (!hasActiveStream && window.location.pathname !== '/login') {
        window.location.href = '/login';
      } else if (hasActiveStream) {
      }
    }
  }

  return response;
};

export default apiFetch;

